import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import {
  FiX,
  FiSend,
  FiImage,
  FiUsers,
  FiUser,
  FiSmartphone,
  FiGlobe,
  FiExternalLink,
} from "react-icons/fi";
import { APP_ROUTES, sendUserPushNotification } from "../../api/userNotificationApi";

export default function SendPushModal({
  isOpen,
  onClose,
  defaultUserId = null,
  defaultUsername = null,
  onSuccess = null,
  initialMode = null,
  initialValues = null,
}) {
  const [mode, setMode] = useState(
    initialMode || (defaultUserId ? "particular" : "bulk")
  );
  const [userId, setUserId] = useState(defaultUserId ? String(defaultUserId) : "");
  const [username, setUsername] = useState(defaultUsername || "");
  const [title, setTitle] = useState(initialValues?.title || "");
  const [message, setMessage] = useState(initialValues?.message || "");
  const [redirectRoute, setRedirectRoute] = useState(
    initialValues?.redirectRoute || "/home"
  );
  const [url, setUrl] = useState(initialValues?.url || "");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialValues?.imageUrl || null);
  const [imageUrlInput, setImageUrlInput] = useState(initialValues?.imageUrl || "");
  const [useUrlForImage, setUseUrlForImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync state whenever modal opens or props change
  useEffect(() => {
    if (isOpen) {
      const activeMode = initialMode || (defaultUserId ? "particular" : "bulk");
      setMode(activeMode);
      setUserId(defaultUserId ? String(defaultUserId) : "");
      setUsername(defaultUsername || "");
      setTitle(initialValues?.title || "");
      setMessage(initialValues?.message || "");
      setRedirectRoute(initialValues?.redirectRoute || "/home");
      setUrl(initialValues?.url || "");
      setImageFile(null);
      setImagePreview(initialValues?.imageUrl || null);
      setImageUrlInput(initialValues?.imageUrl || "");
      setUseUrlForImage(Boolean(initialValues?.imageUrl));
    }
  }, [isOpen, defaultUserId, defaultUsername, initialMode, initialValues]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file (JPG, PNG, WebP)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image file must be less than 5MB");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageUrlInput("");
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrlInput("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanTitle = title.trim();
    const cleanMessage = message.trim();
    const cleanUrl = url.trim();

    if (!cleanTitle) {
      toast.error("Notification Title is required!");
      return;
    }
    if (!cleanMessage) {
      toast.error("Notification Message body is required!");
      return;
    }
    if (mode === "particular") {
      if (!userId || isNaN(Number(userId)) || Number(userId) <= 0) {
        toast.error("Please enter a valid positive Target User ID.");
        return;
      }
    }

    if (cleanUrl) {
      try {
        const parsed = new URL(cleanUrl);
        if (!["http:", "https:"].includes(parsed.protocol)) {
          toast.error("External URL must start with http:// or https://");
          return;
        }
      } catch {
        toast.error("Please enter a valid External URL (e.g. https://example.com)");
        return;
      }
    }

    setLoading(true);

    try {
      let payload;
      const isUploadingFile = Boolean(imageFile);

      if (isUploadingFile) {
        const formData = new FormData();
        formData.append("mode", mode);
        if (mode === "particular") {
          formData.append("userId", String(Number(userId)));
        }
        formData.append("title", cleanTitle);
        formData.append("message", cleanMessage);
        if (redirectRoute) {
          formData.append("redirectRoute", redirectRoute);
        }
        if (cleanUrl) {
          formData.append("url", cleanUrl);
        }
        formData.append("image", imageFile);
        payload = formData;
      } else {
        payload = {
          mode,
          title: cleanTitle,
          message: cleanMessage,
          redirectRoute: redirectRoute || "/home",
          ...(mode === "particular" ? { userId: Number(userId) } : {}),
          ...(cleanUrl ? { url: cleanUrl } : {}),
          ...(useUrlForImage && imageUrlInput.trim()
            ? { imageUrl: imageUrlInput.trim() }
            : {}),
        };
      }

      const res = await sendUserPushNotification(payload);

      if (res?.status || res?.success) {
        const successMsg =
          res.message ||
          (mode === "particular"
            ? `Push sent to user #${userId} successfully!`
            : "Bulk broadcast push dispatched to all users successfully!");
        toast.success(successMsg, { duration: 4000 });
        if (onSuccess) {
          onSuccess(res.data || res);
        }
        onClose();
      } else {
        toast.error(res?.message || "Failed to dispatch push notification.");
      }
    } catch (err) {
      toast.error(err.message || "Server error while sending push notification.");
    } finally {
      setLoading(false);
    }
  };

  const selectedRouteObj = APP_ROUTES.find((r) => r.value === redirectRoute);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 overflow-y-auto"
      onClick={() => !loading && onClose()}
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden my-6 transition-all transform animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-50 via-yellow-50 to-white border-b border-amber-100/70">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#ffffcc] text-[#999900] flex items-center justify-center text-xl shadow-xs">
              🚀
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-base">
                Send Push Notification
              </h3>
              <p className="text-xs text-gray-500">
                FCM push broadcast to mobile app users
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            title="Close modal"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Mode Switcher */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Audience Mode <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 bg-gray-100/80 p-1.5 rounded-xl border border-gray-200">
              <button
                type="button"
                onClick={() => setMode("bulk")}
                className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-bold transition ${
                  mode === "bulk"
                    ? "bg-[#ffff00] text-black shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FiUsers size={14} />
                <span>📢 Bulk (All Users)</span>
              </button>
              <button
                type="button"
                onClick={() => setMode("particular")}
                className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-bold transition ${
                  mode === "particular"
                    ? "bg-[#ffff00] text-black shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FiUser size={14} />
                <span>👤 Particular User</span>
              </button>
            </div>
          </div>

          {/* Target User ID (Only for particular mode) */}
          {mode === "particular" && (
            <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3.5 space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-amber-900">
                  Target User ID <span className="text-red-500">*</span>
                </label>
                {username && (
                  <span className="text-[11px] font-semibold text-amber-800 bg-amber-100/80 px-2 py-0.5 rounded-md">
                    Target: @{username}
                  </span>
                )}
              </div>
              <input
                type="number"
                required
                min="1"
                placeholder="Enter recipient user ID (e.g. 37)"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full px-3.5 py-2 bg-white border border-amber-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
              />
              <p className="text-[11px] text-amber-700">
                This notification will be dispatched only to device tokens registered by this user.
              </p>
            </div>
          )}

          {/* Title & Message */}
          <div className="space-y-3.5">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-700">
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-gray-400 font-mono">
                  {title.length}/100
                </span>
              </div>
              <input
                type="text"
                required
                maxLength={100}
                placeholder="e.g. Platform Upgrade Complete! 🚀"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-700">
                  Message / Body <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-gray-400 font-mono">
                  {message.length}/500
                </span>
              </div>
              <textarea
                rows={3}
                required
                maxLength={500}
                placeholder="e.g. Double mining speed is active for the next 24 hours. Tap to start mining!"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition resize-none leading-relaxed"
              />
            </div>
          </div>

          {/* Deep Link Route & External URL */}
          <div className="grid sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <FiSmartphone className="inline mr-1" size={13} />
                In-App Screen Route
              </label>
              <select
                value={redirectRoute}
                onChange={(e) => setRedirectRoute(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
              >
                {APP_ROUTES.map((route) => (
                  <option key={route.value} value={route.value}>
                    {route.icon} {route.label} ({route.value})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                Opens {selectedRouteObj?.description || "mobile screen"} on tap
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <FiGlobe className="inline mr-1" size={13} />
                External URL <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="url"
                placeholder="https://minezeta.com/promo"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Optional browser link (takes priority on tap if supported)
              </p>
            </div>
          </div>

          {/* Banner Image Upload & Direct URL */}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <FiImage size={14} /> Banner Image (Optional)
              </label>
              <button
                type="button"
                onClick={() => {
                  setUseUrlForImage(!useUrlForImage);
                  if (!useUrlForImage) {
                    setImageFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  } else {
                    setImageUrlInput("");
                    setImagePreview(null);
                  }
                }}
                className="text-[11px] text-[#999900] hover:underline font-medium"
              >
                {useUrlForImage ? "📁 Upload Image File" : "🔗 Use Direct Image URL"}
              </button>
            </div>

            {!useUrlForImage ? (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="w-full text-xs text-gray-500 border border-gray-200 rounded-xl px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#ffffcc] file:text-[#999900] hover:file:bg-[#ffff00] cursor-pointer"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Supported formats: JPG, PNG, WebP (Max 5MB)
                </p>
              </div>
            ) : (
              <div>
                <input
                  type="url"
                  placeholder="https://api.minezeta.com/storage/notification/banner.jpg"
                  value={imageUrlInput}
                  onChange={(e) => {
                    setImageUrlInput(e.target.value);
                    setImagePreview(e.target.value.trim() || null);
                  }}
                  className="w-full px-3.5 py-2 bg-gray-50/50 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
              </div>
            )}

            {imagePreview && (
              <div className="mt-2.5 relative inline-block">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-24 max-w-full rounded-xl object-cover border border-gray-200 shadow-xs"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow hover:bg-red-700 transition"
                  title="Remove image"
                >
                  <FiX size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Live Mobile Push Preview Card */}
          <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl p-4 text-white shadow-inner">
            <div className="flex items-center justify-between text-[11px] text-gray-400 pb-2 border-b border-gray-700/60 mb-2.5">
              <span className="flex items-center gap-1.5 font-medium">
                <FiSmartphone size={12} /> Mobile Push Live Preview
              </span>
              <span className="bg-gray-700 px-2 py-0.5 rounded text-[10px] text-gray-300">
                {mode === "particular" ? `👤 User #${userId || "—"}` : "📢 All Users"}
              </span>
            </div>

            <div className="bg-gray-800/90 border border-gray-700/80 rounded-xl p-3 flex gap-3 shadow-md">
              <div className="w-9 h-9 rounded-xl bg-[#ffff00] text-black font-black flex items-center justify-center text-sm flex-shrink-0 shadow-sm">
                ZX
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <p className="text-xs font-bold text-white truncate">
                    {title.trim() || "Notification Title"}
                  </p>
                  <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap">
                    now
                  </span>
                </div>
                <p className="text-xs text-gray-300 line-clamp-2 mt-0.5 leading-snug">
                  {message.trim() || "Notification message content preview will appear here..."}
                </p>

                {imagePreview && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-gray-700 max-h-28">
                    <img
                      src={imagePreview}
                      alt="Banner Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2 pt-1 border-t border-gray-700/50 text-[10px] text-gray-400">
                  <span className="flex items-center gap-1 font-mono text-[#ffff00]">
                    <span>{selectedRouteObj?.icon}</span> {redirectRoute}
                  </span>
                  {url.trim() && (
                    <span className="flex items-center gap-1 text-blue-400 truncate">
                      <FiExternalLink size={10} /> {url.trim()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Modal Actions Footer */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-black bg-[#ffff00] hover:bg-[#e6e600] rounded-xl shadow-md transition disabled:opacity-60 cursor-pointer"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  <span>Dispatching Push...</span>
                </>
              ) : (
                <>
                  <FiSend size={15} />
                  <span>
                    {mode === "particular"
                      ? `Send to User #${userId || ""}`
                      : "Broadcast to All Users"}
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
